import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, MapPin, X } from 'lucide-react';
import { api } from '../../api/client';

/**
 * The blank Well name and LSD on a field job, and the way to fill them in.
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * Dispatch frequently does not have the LSD when a job is booked, and the crew
 * knows the location for certain because they are standing on it. Measured
 * 2026-09-10: 100 of 153 live jobs on the four live tenants have a blank
 * `well_id_lsd` (TNT 9 of 42), and 11 of those are pending/scheduled/active
 * with crew assigned right now.
 *
 * ─── THE BLANK STATE HAD TO BE MADE VISIBLE FIRST ──────────────────────────
 *
 * FieldJobDetail rendered the Well and LSD rows only when populated
 * (`{(job.lsd || job.well_id_lsd) && …}`), so a blank LSD was INVISIBLE on the
 * one screen whose reader can supply it. There was nothing to press because
 * there was nothing to see. The rows now always render, with an em dash when
 * absent — the repo's standing rule that an absent value renders as `—` and
 * never as `0`, `''`, or a plausible default, applied to the case where the
 * gap is the actionable thing.
 *
 * ─── FILL-ONLY, AND THE FORM SAYS SO BY SHOWING ONLY BLANKS ────────────────
 *
 * The API refuses an overwrite. Rather than render two boxes and let one of
 * them 409, the form renders ONLY the fields that are blank. A field dispatch
 * has already filled is shown as a value with no input beside it, which is the
 * rule stated in the shape of the screen instead of in an error message.
 *
 * The 409 is still handled and still surfaced VERBATIM, because the form can go
 * stale: the office can fill the same field between this screen loading and the
 * hand pressing Save. The API's sentence names the value on file
 * (`LSD is already recorded as "05-12-034-08W5" …`) and nothing written here
 * would be as useful — the hand needs to know what dispatch has before deciding
 * whether to phone them.
 *
 * ─── ONLINE ONLY, AND IT FAILS LOUDLY RATHER THAN QUEUEING ─────────────────
 *
 * Plain `useMutation`, never `useOfflineMutation`. This is not decoration:
 * `well_id_lsd` and `well_name` ARE both in sync's `entityFields.jobs`, so an
 * offline door onto these two columns already exists. API #667's role gate on
 * it refuses a `field_worker` — and NOT an admin, who reaches the same handler
 * because crew membership gates field workers alone, and who uses this app on
 * TNT. A queued job write from an admin would be accepted on reconnect and
 * applied hours later, possibly over a value the office filled in the meantime.
 *
 * Same reasoning and the same treatment as the job status verbs on this screen,
 * whose own comment is the argument: a queued change means the phone says one
 * thing and the office board says another, and nobody finds out until someone
 * asks. Rendered inline, not as a `title` — a tooltip does not exist on a touch
 * device.
 */

export interface WellInfoFillCardProps {
  jobId: string;
  /** The job's current values, either spelling — the payload carries both. */
  lsd: string | null | undefined;
  wellName: string | null | undefined;
  isOnline: boolean;
  /** Called after a successful fill so the caller can invalidate its queries. */
  onFilled: () => void;
}

/** Blank as the API means it: absent, empty, or whitespace only. */
function isBlank(value: string | null | undefined): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

export default function WellInfoFillCard({
  jobId,
  lsd,
  wellName,
  isOnline,
  onFilled,
}: WellInfoFillCardProps) {
  const [open, setOpen] = useState(false);
  const [lsdInput, setLsdInput] = useState('');
  const [wellNameInput, setWellNameInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lsdBlank = isBlank(lsd);
  const wellNameBlank = isBlank(wellName);
  const anythingToFill = lsdBlank || wellNameBlank;

  const fillMutation = useMutation({
    mutationFn: () => {
      // Only blank fields are sent. Sending a value already on file would be a
      // no-op rather than an error, but it would also be a request that claims
      // to write something it cannot.
      const body: { lsd?: string; well_name?: string } = {};
      if (lsdBlank && lsdInput.trim()) body.lsd = lsdInput.trim();
      if (wellNameBlank && wellNameInput.trim()) body.well_name = wellNameInput.trim();
      return api.employee.fillWellInfo(jobId, body);
    },
    onSuccess: () => {
      setError(null);
      setOpen(false);
      setLsdInput('');
      setWellNameInput('');
      onFilled();
    },
    onError: (err: any) => {
      const httpStatus = err?.response?.status;
      if (httpStatus === 404) {
        // The API answers 404 rather than 403 so job ids cannot be probed, which
        // makes it unambiguous here: the job plainly exists, we are looking at
        // it. A generic "not found" would read as a bug in the app rather than
        // the access rule it is.
        setError(
          "You're not on this job's crew, so you can't record its well details. " +
          'Ask dispatch to add you to the crew.',
        );
        return;
      }
      // 409 and 400 both carry the API's own sentence and both are better than
      // anything written here — the 409 names the value on file, which is the
      // whole reason it is a 409 and not a silent ignore.
      setError(
        err?.response?.data?.error ||
        err?.message ||
        'Could not save the well details. Try again.',
      );
      // A 409 means the screen is stale — the office filled it while this form
      // was open. Refresh so the hand sees what is actually on file rather than
      // an input box over a value that now exists.
      if (httpStatus === 409) onFilled();
    },
  });

  const nothingTyped =
    !(lsdBlank && lsdInput.trim()) && !(wellNameBlank && wellNameInput.trim());

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {/*
          Always rendered, em dash when absent. This is the change that makes the
          feature reachable at all — see the header.
        */}
        <div>
          <span className="text-fieldiq-muted text-xs block">Well</span>
          <span className="text-white font-medium">
            {wellNameBlank ? '—' : wellName}
          </span>
        </div>
        <div>
          <span className="text-fieldiq-muted text-xs block">LSD</span>
          <span className="text-white font-medium">{lsdBlank ? '—' : lsd}</span>
        </div>
      </div>

      {anythingToFill && !open && isOnline && (
        <button
          type="button"
          onClick={() => { setError(null); setOpen(true); }}
          className="flex items-center gap-2 text-fieldiq-accent text-sm font-medium"
        >
          <MapPin size={14} />
          {lsdBlank && wellNameBlank
            ? 'Add well name and LSD'
            : lsdBlank
              ? 'Add LSD'
              : 'Add well name'}
        </button>
      )}

      {anythingToFill && !isOnline && (
        <p className="text-fieldiq-muted text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
          You're offline — well details can't be recorded until you reconnect.
        </p>
      )}

      {open && (
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 space-y-3">
          {wellNameBlank && (
            <div>
              <label htmlFor="well-info-well-name" className="text-fieldiq-muted text-xs block mb-1">
                Well name
              </label>
              <input
                id="well-info-well-name"
                type="text"
                value={wellNameInput}
                onChange={(e) => setWellNameInput(e.target.value)}
                maxLength={255}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded px-3 py-2 text-white"
              />
            </div>
          )}
          {lsdBlank && (
            <div>
              <label htmlFor="well-info-lsd" className="text-fieldiq-muted text-xs block mb-1">
                LSD
              </label>
              <input
                id="well-info-lsd"
                type="text"
                value={lsdInput}
                onChange={(e) => setLsdInput(e.target.value)}
                maxLength={100}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded px-3 py-2 text-white"
              />
            </div>
          )}
          <p className="text-fieldiq-muted text-xs">
            Dispatch can change these later. You can add what's missing, but not
            edit what's already been entered.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fillMutation.mutate()}
              disabled={!isOnline || nothingTyped || fillMutation.isPending}
              className="bg-fieldiq-accent text-fieldiq-dark font-medium rounded px-4 py-2 disabled:opacity-50"
            >
              {fillMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setError(null); }}
              className="bg-fieldiq-card border border-fieldiq-border text-white rounded px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm flex-1">{error}</p>
          <button type="button" onClick={() => setError(null)} className="text-red-400" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
